import { useState } from 'react'
import { debounce, sumBy } from 'lodash'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import {
  BulkUpdateIncentivesCompensationPlanMetricsDocument,
  IncentivesCompensationPlanPeriodFragment,
} from 'generated/resources/graphql'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { buildPayoutRulesFromSummary } from 'modules/incentives/lib/payoutRules'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { type CompensationPlanMetric } from './types'

interface Props {
  period: IncentivesCompensationPlanPeriodFragment
  metrics: CompensationPlanMetric[]
  onUpdate: (metrics: CompensationPlanMetric[]) => void
}

const parseUpdatedMetric = (metric: CompensationPlanMetric) => {
  const threshold = metric.threshold != null ? Number(metric.threshold) : null
  const cap = metric.cap != null ? Number(metric.cap) : null
  const weight = Number(metric.weight)

  return { ...metric, threshold, cap, weight }
}

const isPayoutRangeValid = ({ threshold, cap }: CompensationPlanMetric) => {
  if (threshold == null || cap == null) return true
  return cap >= threshold
}

export const useEditableTable = ({ period, metrics, onUpdate }: Props) => {
  const [bulkUpdateMetrics, { loading: isUpdating }] = useMutation(
    BulkUpdateIncentivesCompensationPlanMetricsDocument,
    {
      extractorFn: (data) => data.incentivesMutations.bulkUpdateIncentivesCompensationPlanMetric,
    }
  )

  const [editableMetrics, setEditableMetrics] = useState<CompensationPlanMetric[]>([])

  const debouncedUpdateMetrics = useUnsafeMemo(
    () =>
      debounce(async (updatedMetrics: CompensationPlanMetric[]) => {
        const result = await bulkUpdateMetrics({
          variables: {
            compensationPlanPeriodId: period.id,
            metrics: updatedMetrics.map((metric) => {
              const payoutRules = buildPayoutRulesFromSummary(metric)
              return { id: metric.id, weight: metric.weight, payoutRules }
            }),
          },
        })

        const handled = handleResult(result, () => undefined)

        if (handled.success) {
          onUpdate(updatedMetrics)
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.save_error'),
          })
        }
      }, 1250),
    [period.id, onUpdate, bulkUpdateMetrics]
  )

  const onCellChange = async (
    metric: CompensationPlanMetric
  ): Promise<Record<string, string> | void> => {
    debouncedUpdateMetrics.cancel()

    const parsedMetric = parseUpdatedMetric(metric)
    const newEditableMetrics = editableMetrics.map((m) =>
      m.id === parsedMetric.id ? parsedMetric : m
    )

    setEditableMetrics(newEditableMetrics)

    const weightsSum = sumBy(newEditableMetrics, 'weight')
    if (weightsSum !== 100) {
      const errorMessage =
        weightsSum > 100
          ? i18n.t('incentives.detail.metrics.errors.weights_cant_exceed')
          : i18n.t('incentives.detail.metrics.errors.weights_must_add_up')

      return { weight: errorMessage }
    }

    if (!isPayoutRangeValid(parsedMetric)) {
      return { cap: i18n.t('incentives.detail.metrics.errors.cap_must_be_greater_than_threshold') }
    }

    if (newEditableMetrics.some((m) => !isPayoutRangeValid(m))) return

    debouncedUpdateMetrics(newEditableMetrics)
  }

  const editableColumns = {
    weight: {
      editType: (item: CompensationPlanMetric) =>
        item._policies?.update.can ? 'number' : 'disabled',
      numberConfig: { min: 0, max: 100, units: '%', maxDecimals: 2, locale: i18n.locale },
    },
    threshold: {
      editType: (item: CompensationPlanMetric) =>
        item._policies?.update.can ? 'number' : 'disabled',
      numberConfig: { min: 0, max: 9999, units: '%', maxDecimals: 2, locale: i18n.locale },
    },
    cap: {
      editType: (item: CompensationPlanMetric) =>
        item._policies?.update.can ? 'number' : 'disabled',
      numberConfig: { min: 0, max: 9999, units: '%', maxDecimals: 2, locale: i18n.locale },
    },
  }

  useUnsafeEffect(() => () => debouncedUpdateMetrics.cancel(), [debouncedUpdateMetrics])

  useUnsafeEffect(() => {
    if (editableMetrics.length !== metrics.length) setEditableMetrics(metrics)
  }, [editableMetrics.length, metrics])

  return { editableColumns, isUpdating, onCellChange }
}
